import { useMemo, useState } from 'react';
import { useGetList, Title } from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  AGREED_TOTAL,
  REGIONS,
  Region,
  TAX_NOTE,
  TRANSPORT_NOTE,
  defaultLinesFor,
} from './pricing.constants';
import { TransportSection } from './TransportSection';
import { PreneedSection } from './PreneedSection';

/** A QuickBooks item as the quickbooks data provider hands it back. */
type QbItem = {
  id: string;
  Name?: string;
  Description?: string;
  UnitPrice?: number;
  SalesTaxCodeRef?: { value: string; name: string };
};

/**
 * MUI stacks accordions flush: a hairline `:before` divider between them,
 * `margin: 0` on the collapsed root, and `margin: 16px 0` once expanded.
 *
 * The collapsed `margin: 0` is why a parent Stack's spacing does nothing here —
 * it is overridden on the child itself. So the gap has to be set on the panel,
 * in both states, or the sections either sit flush or shift as one opens.
 */
const panelSx = {
  borderRadius: 1,
  '&:before': { display: 'none' },
  marginBottom: 1.5,
  '&.Mui-expanded': {
    marginTop: 0,
    marginBottom: 1.5,
  },
};

const money = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 });

/**
 * Internal price list. Reference only — this page computes and records nothing.
 *
 * Prices are read live from QuickBooks rather than from Postgres, because
 * `public.items` is empty and QuickBooks is the source of truth. That means the
 * page can never show a stale price, at the cost of depending on QuickBooks
 * being reachable.
 */
export const PricingPage = () => {
  const [region, setRegion] = useState<Region>('ontario');
  /**
   * One panel open at a time, so whichever the agent picked is always near the
   * top of the page instead of below two other expanded sections. At-need is
   * open on arrival because most calls are at-need.
   */
  const [open, setOpen] = useState<'atneed' | 'transport' | 'preneed' | false>('atneed');

  // The whole catalogue in one request, then picked apart locally: the item
  // filter semantics of the QuickBooks proxy are not worth depending on for a
  // handful of ids, and the catalogue is small.
  const { data, isLoading, error } = useGetList<QbItem>('items', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'Name', order: 'ASC' },
  });

  const byId = useMemo(() => {
    const map = new Map<string, QbItem>();
    (data ?? []).forEach((item) => map.set(String(item.id), item));
    return map;
  }, [data]);

  const lines = useMemo(
    () =>
      defaultLinesFor(region).map((line) => {
        const item = byId.get(line.item);
        return {
          id: line.item,
          name: item?.Name ?? `Item ${line.item}`,
          price: (item?.UnitPrice ?? 0) * (line.quantity ?? 1),
          tax: item?.SalesTaxCodeRef?.name ?? '—',
          missing: !item,
        };
      }),
    [region, byId],
  );

  const lineSum = lines.reduce((total, line) => total + line.price, 0);
  const agreed = AGREED_TOTAL[region];
  const anyMissing = lines.some((line) => line.missing);
  // Only meaningful once every line resolved; a missing item would fake a gap.
  const gap = !anyMissing && Math.abs(lineSum - agreed) >= 1 ? lineSum - agreed : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Title title="Pricing" />

      <Stack direction="row" alignItems="center" sx={{ mb: 1, gap: 2 }}>
        <Typography variant="h5">Pricing</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={region}
          onChange={(_, value: Region | null) => value && setRegion(value)}
        >
          {REGIONS.map((r) => (
            <ToggleButton key={r.id} value={r.id}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Internal reference. Prices are read live from QuickBooks; the lines are the same
        ones a new contract starts with.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn't reach QuickBooks, so no prices are shown. Nothing here is cached —
          rather than display a figure that might be out of date, the page shows none.
        </Alert>
      )}

      <Accordion
        sx={panelSx}
        expanded={open === 'atneed'}
        onChange={(_, isOpen) => setOpen(isOpen ? 'atneed' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ flex: 1 }}>At-need — default contract</Typography>
          {/* The headline number in the summary, so the most-asked question is
              answered without expanding anything. */}
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }} color="text.secondary">
            {money(agreed)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Line</TableCell>
                    <TableCell>Tax</TableCell>
                    <TableCell align="right">Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        {line.name}
                        {line.missing && (
                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                            not found in QuickBooks
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {line.tax}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {line.missing ? '—' : money(line.price)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}>
                      <strong>Quote this — all in</strong>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{money(agreed)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {gap !== 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  These lines total <strong>{money(lineSum)}</strong>, but the agreed
                  quote is <strong>{money(agreed)}</strong> — a {money(Math.abs(gap))}{' '}
                  difference. Quote {money(agreed)}; a contract built today renders{' '}
                  {money(lineSum)}, so the line items still need reconciling.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Tax.</strong> {TAX_NOTE[region]} The total above is all in.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Transport.</strong> {TRANSPORT_NOTE[region]}
              </Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        sx={panelSx}
        expanded={open === 'transport'}
        onChange={(_, isOpen) => setOpen(isOpen ? 'transport' : false)}
        // The map is a Google Map, and one initialised inside a collapsed panel
        // measures its container as zero-height and stays broken after it opens.
        // Unmounting on collapse means it always builds against a laid-out box.
        TransitionProps={{ unmountOnExit: true }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ flex: 1 }}>Transport</Typography>
          <Typography color="text.secondary" variant="body2">
            Check an address
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TransportSection region={region} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        sx={panelSx}
        expanded={open === 'preneed'}
        onChange={(_, isOpen) => setOpen(isOpen ? 'preneed' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ flex: 1 }}>Preneed</Typography>
          <Typography color="text.secondary" variant="body2">
            Monthly plans
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PreneedSection region={region} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PricingPage;
